import React from 'react';
import styled from 'styled-components';
import Typography from '@/components/core/Typography/Typography';
import { useTranslations } from 'next-intl';
import {
    ButtonsFrame,
    PageWrapper2VerticalSections,
    StyledButton,
    StyledMainFrame,
    Title,
    WideFrame
} from '@/app/[locale]/(welcome)/auth/_styled';
import { useTimer } from 'react-timer-hook';
import { MoonOneLogo, NeedHelpBlock } from '@/app/[locale]/(welcome)/_components';
import { device, HideAbove, HideBelow } from '@/components/Media';
import { resetPassword } from '@/actions/auth/resetPassword';
import useToast, { ToastNotification } from '@/hooks/useToast';

const StyledWideFrame = styled(WideFrame)`
    @media screen and ${device.laptopS} {
        flex: unset;
        gap: ${({ theme }) => theme.spacing['2xs']};
    }
`;

const GreyTypography = styled(Typography)`
    display: block;
    white-space: pre-line;
    text-align: center;
`;

const ExpiredTypography = styled(Typography)`
    text-align: center;
    color: ${({ theme }) => theme.colors.palette.red_300};
`;

const ResetLinkTypography = styled(Typography)`
    display: block;
    white-space: pre-line;
    text-align: center;
    line-height: 32px;
`;

const ActiveLink = styled(ResetLinkTypography)`
    color: ${({ theme }) => theme.colors.palette.blue_500};
`;

const Email = styled(Typography)`
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
`;

const LINK_MINUTES_TTL = 30;

export default function SentInfo({ email }) {
    const t = useTranslations();
    const [queueNotification] = useToast();

    const resendLink = async () => {
        const success = await resetPassword(email).catch(console.error);

        if (success) {
            const expirationDate = new Date();
            expirationDate.setMinutes(expirationDate.getMinutes() + LINK_MINUTES_TTL);
            timer.restart(expirationDate);
        } else {
            queueNotification({ description: <>{t('common.error')}</> } as ToastNotification);
        }
    };

    const expirationDate = new Date();
    expirationDate.setMinutes(expirationDate.getMinutes() + LINK_MINUTES_TTL);
    const timer = useTimer({
        autoStart: true,
        expiryTimestamp: expirationDate
    });

    const sentEmailText = t.rich('auth.resetPasswordEmailWasSent', {
        email: email,
        br: () => <br />,
        bold: (chunks) => <Email variant='uiType' size='md' modifier='S'>{chunks}</Email>
    });

    const activeLinkText = t('auth.expirationText', {
        minutes: timer.minutes.toString().padStart(2, '0'),
        seconds: timer.seconds.toString().padStart(2, '0')
    });

    return (
        <PageWrapper2VerticalSections>
            <HideAbove md>
                <MoonOneLogo />
            </HideAbove>

            <StyledMainFrame>
                <HideBelow md>
                    <Title variant='uiType' size='3xl' modifier='S'>{t('auth.checkYourEmail')}</Title>
                </HideBelow>
                <HideAbove md>
                    <Title variant='uiType' size='xl' modifier='S'>{t('auth.checkYourEmail')}</Title>
                </HideAbove>

                <StyledWideFrame>
                    <HideBelow md>
                        <GreyTypography variant='uiType' size='md' modifier='S'>{sentEmailText}</GreyTypography>
                    </HideBelow>
                    <HideAbove md>
                        <GreyTypography variant='uiType' size='sm' modifier='S'>{sentEmailText}</GreyTypography>
                    </HideAbove>

                    {
                        timer.totalSeconds > 0 ? (
                            <>
                                <HideBelow md>
                                    <ActiveLink variant='uiType' size='md' modifier='R'>{activeLinkText}</ActiveLink>
                                </HideBelow>
                                <HideAbove md>
                                    <ActiveLink variant='uiType' size='sm' modifier='R'>{activeLinkText}</ActiveLink>
                                </HideAbove>
                            </>
                        ) : (
                            <>
                                <ExpiredTypography variant='uiType' size='sm' modifier='S'>
                                    {t('auth.expiredText')}
                                </ExpiredTypography>

                                <ButtonsFrame>
                                    <StyledButton
                                      type='button'
                                      variant='filled'
                                      size='sm'
                                      innerText={t('auth.resetPasswordResentLink')}
                                      onClick={resendLink}
                                    />
                                </ButtonsFrame>
                            </>
                        )
                    }
                </StyledWideFrame>
            </StyledMainFrame>

            <NeedHelpBlock />
        </PageWrapper2VerticalSections>
    );
}
